import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2, Plus, PlusCircle } from "lucide-react";
import { Fragment } from "react/jsx-runtime";
import {
  type ApprovalObject,
  createApprovalPolicy,
  startsWithVowelSound,
} from "shared";
import { toast } from "sonner";
import { IconButton } from "@/components/icon-button";
import { RenderStatusBadge } from "@/components/table/cells/RenderStatusBadge";
import { Button } from "@/components/ui/button";
import { FormStepSeparator } from "@/components/ui/form-step-seperator";
import {
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { UserAvatar } from "@/components/user-avatar";
import { useAppForm } from "@/hooks/useCreateForm";
import { queryClient, trpc } from "@/lib/trpcClient";
import { MemberSelect } from "./-components/member-select";
import { RoleIcon } from "./-components/role-icon";
import { RoleSelect } from "./-components/role-select";

export const Route = createFileRoute("/_authed/_org/policies/approval/create")({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = Route.useNavigate();

  const { mutate: createPolicy, isPending } = useMutation(
    trpc.approvalPolicy.create.mutationOptions({
      onError: (error) => {
        toast.error("Failed to create policy", {
          description: error.message,
        });
      },
      onSuccess: () => {
        toast.success("Policy created successfully");
        navigate({
          to: "/policies/approval",
        });
        form.reset();
        queryClient.invalidateQueries(trpc.approvalPolicy.list.queryOptions());
      },
    }),
  );

  const form = useAppForm({
    defaultValues: {
      approvers: [
        {
          role: "admin",
          type: "role",
        },
      ] as ApprovalObject[],
      description: "" as string | null,
      groupOperator: "all" as "all" | "any",
      name: "",
    },
    onSubmit: ({ value }) => createPolicy(value),
    validators: {
      onSubmit: createApprovalPolicy,
    },
  });

  return (
    <div className="flex h-full flex-col">
      <SheetHeader className="border-b px-6 py-6 pb-2">
        <SheetTitle>Create an Approval Policy</SheetTitle>
        <SheetDescription>
          Approval policies control who needs to review transactions on
          different cards.
        </SheetDescription>
      </SheetHeader>

      <form
        className="flex min-h-0 flex-1 flex-col"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        {/* Scrollable content area with fixed max height */}
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-6">
          <form.AppField
            children={(field) => (
              <field.TextField label="Name" required={true} />
            )}
            name="name"
          />

          <form.AppField
            children={(field) => (
              <field.TextAreaField label="Description" required={false} />
            )}
            name="description"
          />

          <form.Subscribe selector={(state) => state.values.approvers}>
            {(approvers) => (
              <FormStepSeparator
                description={
                  <div className="flex items-center gap-1.5">
                    <span className="shrink-0 pb-1">Require Approval From</span>

                    {approvers.length > 1 && (
                      <form.AppField name="groupOperator">
                        {(field) => (
                          <field.SelectField
                            className="rounded-md bg-neutral-100 text-lg text-neutral-700 shadow-none [&>button]:px-2"
                            options={[
                              {
                                label: "Any Of",
                                value: "any",
                              },
                              {
                                label: "All Of",
                                value: "all",
                              },
                            ]}
                          />
                        )}
                      </form.AppField>
                    )}
                  </div>
                }
              />
            )}
          </form.Subscribe>

          <form.Subscribe selector={(state) => state.values.groupOperator}>
            {(groupOperator) => (
              <div className="flex flex-col">
                <form.Field mode="array" name="approvers">
                  {(field) => (
                    <>
                      {field.state.value.map((approver, index) => (
                        <Fragment key={index}>
                          <div className="flex w-full items-center rounded-md border border-border bg-neutral-50 px-4 py-2 shadow-xs">
                            {approver.type === "role" ? (
                              <>
                                <RoleIcon
                                  role={approver.role}
                                  className="mr-4"
                                />

                                <div className="flex items-center gap-1.5 text-neutral-600">
                                  Any member that is{" "}
                                  {startsWithVowelSound(approver.role)
                                    ? "an"
                                    : "a"}{" "}
                                  <RenderStatusBadge
                                    status={approver.role}
                                    className="mt-1"
                                  />
                                </div>
                              </>
                            ) : (
                              <UserAvatar
                                userId={approver.userId}
                                className="gap-4! p-0! [&>span]:size-10 [&>span]:rounded-full"
                              />
                            )}

                            {field.state.value.length > 1 && (
                              <IconButton
                                icon="x"
                                tooltip="Remove Item"
                                className="ml-auto"
                                onClick={() => field.removeValue(index)}
                              />
                            )}
                          </div>

                          {index !== field.state.value.length - 1 && (
                            <div className="my-2 flex items-center gap-2 font-semibold text-[0.6rem] text-neutral-400 uppercase">
                              <div className="h-px grow bg-border" />
                              <span>
                                {groupOperator === "all" ? "and" : "or"}
                              </span>
                              <div className="h-px grow bg-border" />
                            </div>
                          )}
                        </Fragment>
                      ))}

                      <div className="group mt-4 flex h-8 justify-center text-neutral-500 text-sm">
                        <RoleSelect
                          onValueChange={(role) =>
                            field.pushValue({
                              role,
                              type: "role",
                            })
                          }
                          unavailableRoles={field.state.value
                            .filter((a) => a.type === "role")
                            .map((a) => a.role)}
                        >
                          <button
                            className="flex grow items-center justify-center gap-2 rounded-l border border-neutral-300 border-r-0 border-dashed hover:border-solid hover:bg-neutral-50"
                            type="button"
                          >
                            <Plus className="size-4 text-neutral-400" />
                            Add Role
                          </button>
                        </RoleSelect>

                        <div className="h-full border-neutral-300 border-r border-dashed group-hover:border-solid" />

                        <MemberSelect
                          onValueChange={(userId) =>
                            field.pushValue({
                              type: "member",
                              userId,
                            })
                          }
                          unavailableUsers={field.state.value
                            .filter((a) => a.type === "member")
                            .map((a) => a.userId)}
                        >
                          <button
                            className="flex grow items-center justify-center gap-2 rounded-r border border-neutral-300 border-l-0 border-dashed hover:border-solid hover:bg-neutral-50"
                            type="button"
                          >
                            <Plus className="size-4 text-neutral-400" />
                            Add Member
                          </button>
                        </MemberSelect>
                      </div>
                    </>
                  )}
                </form.Field>
              </div>
            )}
          </form.Subscribe>
        </div>
        <div className="flex min-h-16 flex-row items-center justify-start border-t p-6">
          <Button
            className="font-semibold"
            disabled={isPending}
            size="default"
            type="submit"
          >
            {isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <PlusCircle className="mr-2 h-4 w-4" />
            )}
            Create
          </Button>
        </div>
      </form>
    </div>
  );
}
